import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faPlus } from "@fortawesome/pro-regular-svg-icons";
import moment from "moment";

import { StatusCode } from "api/protocols";
import {
  DeletePricingSheet,
  GetPricingSheet,
} from "api/rpc/2024-04/facilityAdmin/teesheet/pricingSheet";
import { showError, showSuccess } from "redux/actions/ui";
import { TTeesheetPricingSheet } from "redux/reducers/models/teesheet";
import { convertTime } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";

import Page from "components/page/Page";
import Checkbox from "components/form/checkbox/Checkbox";
import Callout from "components/callout/Callout";
import Input from "components/form/input/Input";
import { Select } from "components/select/index";
import Portal from "elements/Portal";
import DataTable from "components/dataTable/DataTable";
import Popup from "components/popup/Popup";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { ButtonNew } from "components/buttonNew";
import classNames from "classnames";

import BulkEditPricingSheetModal from "./components/BulkEditPricingSheetModal";
import DuplicatePricingSheetModal from "./components/DuplicatePricingSheetModal";
import {
  ALL_PRICING_SHEET_APPLICATIONS,
  ALL_PRICING_SHEET_DAYS,
  CHECK_IN_CONFIG_PATH,
  getPricingSheetApplicationLabel,
  getStoredApplication,
  getStoredListApplication,
  getStoredListDayFilter,
  persistListApplication,
  persistListDayFilter,
  persistPricingSheetFilters,
  PRICING_SHEET_APPLICATIONS,
  PricingSheetApplicationFilter,
  PricingSheetApplicationValue,
  PricingSheetDayFilter,
  PricingSheetSavedLocationState,
} from "./pricingSheetApplications";

import "../templates/TeeSheetTemplate.scss";
import "components/scrollTabsLayout/scrollTabsLayout.scss";
import "./teeSheetPricingSheet.scss";

export enum Day {
  Monday = 1,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
  Sunday,
  Holiday = 99,
}

export type DayValue = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 99;

interface IPricingSheetListState {
  pricingSheetIds: number[];
  showDuplicateModal: boolean;
  selectAllPricingSheets: boolean;
}

type SavedCalloutState = {
  application: PricingSheetApplicationValue;
  dayOfWeek: string;
};

export default function PricingSheets() {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const facilityStore = useAppSelector(store => store.facilityStore);
  const dispatch = useAppDispatch();

  const { Option } = Select;
  const isMultiCourse = (facilityStore.facility?.courses?.length ?? 0) > 1;

  const [pricingSheets, setPricingSheets] = useState<TTeesheetPricingSheet[]>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedApplication, setSelectedApplication] =
    useState<PricingSheetApplicationFilter>(getStoredListApplication);
  const [filterDayState, setFilterDayState] = useState<PricingSheetDayFilter>(getStoredListDayFilter);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const {
    state: deletePricingSheetWarning,
    updateModal: updateDeletePricingSheetWarning,
    closeModal: closeDeletePricingSheetWarning,
  } = useModal();

  const [state, setState] = useState<IPricingSheetListState>({
    pricingSheetIds: [],
    showDuplicateModal: false,
    selectAllPricingSheets: false,
  });
  const [savedCallout, setSavedCallout] = useState<SavedCalloutState | null>(null);

  useEffect(() => {
    const locationState = location.state as PricingSheetSavedLocationState | undefined;
    if (!locationState?.showPricingSheetSavedCallout) {
      return;
    }

    setSavedCallout({
      application: locationState.application ?? getStoredApplication(),
      dayOfWeek: locationState.dayOfWeek ?? "Monday",
    });

    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [history, location.hash, location.pathname, location.search, location.state]);

  useEffect(() => {
    persistListDayFilter(filterDayState);
  }, [filterDayState]);

  useEffect(() => {
    persistListApplication(selectedApplication);
  }, [selectedApplication]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadPricingSheets(source.token);
    return () => source.cancel();
  }, []);

  async function loadPricingSheets(token?: CancelToken) {
    if (pricingSheets !== undefined) {
      setPricingSheets(undefined);
    }
    const res = await GetPricingSheet({}, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading pricing sheets.")));
    }

    setPricingSheets(res.status !== StatusCode.OK ? [] : res.data);
  }

  const filteredPricingSheets = useMemo(() => {
    if (!pricingSheets) {
      return undefined;
    }

    const normalizedSearch = searchQuery.trim().toLowerCase();

    return pricingSheets.filter(sheet => {
      if (
        selectedApplication !== ALL_PRICING_SHEET_APPLICATIONS &&
        sheet.application !== selectedApplication
      ) {
        return false;
      }

      if (filterDayState !== ALL_PRICING_SHEET_DAYS && sheet.day_of_week_number !== filterDayState) {
        return false;
      }

      if (normalizedSearch && !sheet.title?.toLowerCase().includes(normalizedSearch)) {
        return false;
      }

      return true;
    });
  }, [pricingSheets, selectedApplication, filterDayState, searchQuery]);

  function clearRowSelection() {
    setState(prevState => ({
      ...prevState,
      selectAllPricingSheets: false,
      pricingSheetIds: [],
    }));
  }

  function handleApplicationChange(application: PricingSheetApplicationFilter) {
    setSelectedApplication(application);
    clearRowSelection();
  }

  function handleDayChange(value: string | number) {
    if (value === ALL_PRICING_SHEET_DAYS) {
      setFilterDayState(ALL_PRICING_SHEET_DAYS);
    } else {
      setFilterDayState(Number(value) as DayValue);
    }
    clearRowSelection();
  }

  function goToNewPricingSheet() {
    history.push("/admin/settings/tee-sheet/pricing-sheet/new");
  }

  function handleCheckboxClick(pricingSheetId: number) {
    const pricingSheetIds = [...state.pricingSheetIds];
    const foundIndex = pricingSheetIds.indexOf(pricingSheetId);
    if (foundIndex === -1) {
      pricingSheetIds.push(pricingSheetId);
    } else {
      pricingSheetIds.splice(foundIndex, 1);
    }
    setState(prevState => ({
      ...prevState,
      pricingSheetIds,
      selectAllPricingSheets: false,
    }));
  }

  function handleSetAllClick() {
    const everyPricingSheetId = filteredPricingSheets?.map(sheet => sheet.id) ?? [];
    setState(prevState => ({
      ...prevState,
      pricingSheetIds: everyPricingSheetId,
      selectAllPricingSheets: true,
    }));
  }

  function handleClearClick() {
    setState(prevState => ({ ...prevState, pricingSheetIds: [], selectAllPricingSheets: false }));
  }

  function openDuplicateModal() {
    setState(prevState => ({
      ...prevState,
      showDuplicateModal: true,
    }));
  }

  function closeDuplicateModal() {
    setState(prevState => ({
      ...prevState,
      showDuplicateModal: false,
    }));
  }

  function handleDuplicateSuccess() {
    void loadPricingSheets();
    setState(prevState => ({
      ...prevState,
      pricingSheetIds: [],
      showDuplicateModal: false,
      selectAllPricingSheets: false,
    }));
  }

  function handleBulkEditSuccess(updatedCount: number) {
    dispatch(showSuccess(t("Updated {{count}} pricing sheet(s) successfully", { count: updatedCount })));
    setBulkEditOpen(false);
    void loadPricingSheets();
    clearRowSelection();
  }

  function handleSelectAllCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;

    if (!checked) {
      handleClearClick();
    } else {
      handleSetAllClick();
    }
  }

  async function handleDeletePricingSheets() {
    const deleteRes = await DeletePricingSheet({ id: state.pricingSheetIds }, true);

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting selected pricing sheets")));
      return;
    }

    dispatch(showSuccess(t("Pricing Sheets deleted successfully")));
    void loadPricingSheets();
    closeDeletePricingSheetWarning();
    setState(prevState => ({ ...prevState, pricingSheetIds: [], selectAllPricingSheets: false }));
  }

  function formatSheetDateRange(sheet: TTeesheetPricingSheet) {
    if (!sheet.start_date || !sheet.end_date) {
      return "";
    }

    return `${moment(sheet.start_date, "MM-DD").format("MMM D")} - ${moment(sheet.end_date, "MM-DD").format("MMM D")}`;
  }

  function formatSheetTimeRange(sheet: TTeesheetPricingSheet) {
    if (!sheet.start_time || !sheet.end_time) {
      return "";
    }

    return `${convertTime(sheet.start_time)} – ${convertTime(sheet.end_time)}`;
  }

  function dismissSavedCallout() {
    setSavedCallout(null);
  }

  function handleSetUpAtCheckIn() {
    if (savedCallout) {
      persistPricingSheetFilters(savedCallout.application, savedCallout.dayOfWeek);
    }

    dismissSavedCallout();
    history.push(CHECK_IN_CONFIG_PATH);
  }

  const tableContent = (
    <div className="pricing-sheets-table-wrap">
      {filteredPricingSheets !== undefined && filteredPricingSheets.length === 0 ? (
        <div className="pricing-sheets-empty-state">
          <MessageDisplay
            centered
            icon="magnifying-glass"
            title={t("No pricing sheet found")}
            content={t("You can create a new pricing sheet – it will appear here.")}
            actions={[
              {
                label: t("+ New pricing sheet"),
                action: goToNewPricingSheet,
                type: "primary",
              },
            ]}
          />
        </div>
      ) : (
        <DataTable
          overflow
          columns={[
            {
              label: "",
              width: "5%",
              content: (
                <div>
                  <Checkbox
                    id="selectAllPricingSheets"
                    size="medium"
                    checked={state.selectAllPricingSheets}
                    onChange={handleSelectAllCheckboxChange}
                    labelHidden
                  />
                </div>
              ),
            },
            { label: t("Pricing sheet") },
            ...(selectedApplication === ALL_PRICING_SHEET_APPLICATIONS ? [{ label: t("Rate type") }] : []),
            ...(filterDayState === ALL_PRICING_SHEET_DAYS ? [{ label: t("Day") }] : []),
            ...(isMultiCourse ? [{ label: t("Course") }] : []),
            { label: t("Date range") },
            { label: t("Time range") },
          ]}
          loading={filteredPricingSheets === undefined}
        >
          {filteredPricingSheets?.map(sheet => (
            <tr
              key={sheet.id}
              className="clickable"
              onClick={() =>
                history.push("/admin/settings/tee-sheet/pricing-sheet/" + String(sheet.id))
              }
            >
              <td onClick={e => e.stopPropagation()}>
                <Checkbox
                  onChange={() => handleCheckboxClick(sheet.id)}
                  size="medium"
                  checked={state.pricingSheetIds.includes(sheet.id)}
                />
              </td>
              <td>
                <span className="pricing-sheets-title-cell">{sheet.title}</span>
              </td>
              {selectedApplication === ALL_PRICING_SHEET_APPLICATIONS ? (
                <td>{t(getPricingSheetApplicationLabel(String(sheet.application)))}</td>
              ) : null}
              {filterDayState === ALL_PRICING_SHEET_DAYS ? <td>{t(sheet.day_of_week)}</td> : null}
              {isMultiCourse ? <td>{sheet.course?.full_name}</td> : null}
              <td>{formatSheetDateRange(sheet)}</td>
              <td>{formatSheetTimeRange(sheet)}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );

  const primaryAction = {
    content: <><FontAwesomeIcon icon={faPlus} /> <span className="ml-2">{t("New pricing sheet")}</span></>,
    action: goToNewPricingSheet,
  };

  return (
    <>
      <Page
        title={t("Pricing sheets")}
        subtitle={t("View and manage pricing sheets by rate type and day.")}
        primaryAction={primaryAction}
      >
        <hr />
        <div className="pricing-sheets-list ui-scroll-tabs-layout ui-scroll-tabs-layout--size-sm">
          <div className="ui-scroll-tabs-layout-tabs-list">
            <button
              type="button"
              className={classNames("ui-scroll-tabs-layout-tab", {
                "ui-scroll-tabs-layout-tab-active": selectedApplication === ALL_PRICING_SHEET_APPLICATIONS,
              })}
              onClick={() => handleApplicationChange(ALL_PRICING_SHEET_APPLICATIONS)}
            >
              <span className="ui-scroll-tabs-layout-tab-text">
                <span className="ui-scroll-tabs-layout-tab-label">{t("All")}</span>
              </span>
            </button>
            {PRICING_SHEET_APPLICATIONS.map(app => (
              <button
                key={app.value}
                type="button"
                className={classNames("ui-scroll-tabs-layout-tab", {
                  "ui-scroll-tabs-layout-tab-active": selectedApplication === app.value,
                })}
                onClick={() => handleApplicationChange(app.value)}
              >
                <span className="ui-scroll-tabs-layout-tab-text">
                  <span className="ui-scroll-tabs-layout-tab-label">{t(app.label)}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="ui-scroll-tabs-layout-content-list pricing-sheets-list-content">
            <div className="pricing-sheets-toolbar">
              <div className="flex items-center gap-2">
                <p>{t("Day of week:")}</p>
                <div className="pricing-sheets-toolbar-day">
                  <Select
                    defaultValue={filterDayState}
                    onChange={(value: string | number) => handleDayChange(value)}
                    leftIcon={<FontAwesomeIcon icon={faCalendar} style={{ marginRight: 8 }} />}
                  >
                    <Option value={ALL_PRICING_SHEET_DAYS}>{t("All")}</Option>
                    <Option value={Day.Monday}>{t("Monday")}</Option>
                    <Option value={Day.Tuesday}>{t("Tuesday")}</Option>
                    <Option value={Day.Wednesday}>{t("Wednesday")}</Option>
                    <Option value={Day.Thursday}>{t("Thursday")}</Option>
                    <Option value={Day.Friday}>{t("Friday")}</Option>
                    <Option value={Day.Saturday}>{t("Saturday")}</Option>
                    <Option value={Day.Sunday}>{t("Sunday")}</Option>
                    <Option value={Day.Holiday}>{t("Holiday")}</Option>
                  </Select>
                </div>
              </div>

              <div className="pricing-sheets-toolbar-search">
                <Input
                  labelHidden
                  placeholder={t("Search")}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onSearchClear={() => setSearchQuery("")}
                />
                <div
                  className={classNames("pricing-sheets-bulk-edit-slot", {
                    "is-visible": state.pricingSheetIds.length > 0,
                  })}
                >
                  <div className="pricing-sheets-selection-actions">
                    <ButtonNew type="secondary" size="medium" onClick={() => setBulkEditOpen(true)}>
                      {t("Bulk Edit")}
                    </ButtonNew>
                    <ButtonNew type="secondary" size="medium" onClick={() => openDuplicateModal()}>
                      {t("Duplicate")}
                    </ButtonNew>
                    <ButtonNew
                      type="error-outline"
                      size="medium"
                      onClick={() => updateDeletePricingSheetWarning({ isOpen: true })}
                    >
                      {t("Delete")}
                    </ButtonNew>
                  </div>
                </div>
              </div>
            </div>

            {tableContent}
          </div>
        </div>
      </Page>

      <BulkEditPricingSheetModal
        open={bulkEditOpen}
        pricingSheetIds={state.pricingSheetIds}
        courses={facilityStore?.facility?.courses ?? []}
        isMultiCourse={isMultiCourse}
        onClose={() => setBulkEditOpen(false)}
        onSuccess={handleBulkEditSuccess}
      />

      <DuplicatePricingSheetModal
        open={state.showDuplicateModal}
        onClose={closeDuplicateModal}
        onSuccess={handleDuplicateSuccess}
        pricingSheetIds={state.pricingSheetIds}
        sourceDayLabel={filterDayState === ALL_PRICING_SHEET_DAYS ? "" : Day[filterDayState]}
        pricingSheets={pricingSheets}
        courses={facilityStore?.facility?.courses ?? []}
        defaultCourseId={facilityStore?.facility?.courses?.[0]?.id ?? 0}
      />

      <Portal isMounted={deletePricingSheetWarning.isOpen}>
        <Popup
          okText={t("Confirm")}
          onCancel={() => updateDeletePricingSheetWarning({ isOpen: false })}
          onOk={() => handleDeletePricingSheets()}
          title={t("Delete Pricing Sheets")}
          type="warning"
          description={t("Are you sure you want to delete these pricing sheets?")}
          open={deletePricingSheetWarning.isOpen}
          okDisabled={state.pricingSheetIds?.length === 0}
        >
          {state?.pricingSheetIds?.length === 0 && (
            <Callout
              type="error"
              title={t("No Pricing Sheets Selected")}
              content={t("Select the check boxes of the pricing sheets you wish to delete and try again")}
            />
          )}
        </Popup>
      </Portal>

      <Portal isMounted={savedCallout != null}>
        <div className="pricing-sheets-saved-callout">
          <Callout
            type="success"
            title={t("Pricing sheet saved")}
            description={t("Set up this rate in check-in so staff can charge it.")}
            onClose={dismissSavedCallout}
            actions={{
              secondary: {
                label: t("Not now"),
                onClick: dismissSavedCallout,
              },
              primary: {
                label: t("Set up at check-in"),
                buttonType: "secondary",
                onClick: handleSetUpAtCheckIn,
              },
            }}
          />
        </div>
      </Portal>
    </>
  );
}

export function DayTab({
  title,
  value,
  compare,
  onClick,
}: {
  title: string;
  value: DayValue;
  compare: DayValue | string;
  onClick: (value: DayValue) => void;
}): JSX.Element {
  return (
    <button
      className="flex-1 py-2 rounded-t-m"
      style={
        compare === value
          ? { backgroundColor: "#e7e7e7", borderTopLeftRadius: "7px", borderTopRightRadius: "7px" }
          : { backgroundColor: "#ffffff" }
      }
      onClick={() => onClick(value)}
    >
      {title}
    </button>
  );
}
